import { useEffect, useMemo, useState } from 'react';
import { Alert, BackHandler, Platform, Pressable, Text, View, useWindowDimensions } from 'react-native';
import { useIsFocused, useLocalSearchParams, useRouter } from 'expo-router';
import { ouvirVoltarAoPlanner, tomarVoltarAndroid } from '../../lib/voltarPlanner';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useDoc } from '../../store/store';
import { ordenarTarefas, tasksOn } from '../../store/planner';
import { Ordenavel, ScrollArrastavel } from '../../components/Arrastar';
import { addDays, horaTela, labelDia, nomeMes, parseYmd, today } from '../../lib/date';
import { TaskCardAcoes } from '../../components/TaskCard';
import { WeekStrip } from '../../components/WeekStrip';
import { useFeriados } from '../../components/useFeriados';
import { ROTULO, bandeira } from '../../lib/feriados';
import { nomePais } from '../../lib/feriadosMundo';
import { useTaskSheet, diaAberto } from '../../components/TaskSheet';
import { C, useCores } from '../../components/theme';
import { AtualizacaoBanner } from '../../components/AtualizacaoBanner';
import { CronoCard } from '../../screens/Estudos';
import { MonthSheet } from '../../components/MonthSheet';
import { SearchSheet } from '../../components/SearchSheet';
import { FeriadosSheet } from '../../components/FeriadosSheet';
import { t } from '../../lib/i18n';
import { ViagemHoje, useViagemDoDia } from '../../components/ViagemHoje';
import { Caixinhas } from '../../components/Caixinhas';
import { Pendentes } from '../../components/Pendentes';
import { AguardandoEnvio } from '../../components/EnvioPlanner';   // texto 10 (E2): itens aguardando envio
import { abrirGrupos } from '../../components/Grupos';
import { usePendentes } from '../../lib/grupos';
import { useOrganizar } from '../../lib/organizar';
import { Arquivados, useSelecao } from '../../components/Selecao';
import { excluirTarefa } from '../../store/planner';
import { isRepeating } from '../../lib/recur';
import { useCaixas } from '../../lib/caixas';
export { ErrorBoundary } from '../../components/Falha';

export default function Hoje() {
  const ins = useSafeAreaInsets();
  const router = useRouter();
  const params = useLocalSearchParams<{ date?: string }>();
  const { tasks } = useDoc('planner');
  const ajustes = useDoc('ajustes');
  const m = useCaixas();
  const { accent, situacao } = useCores();
  const feriados = useFeriados();
  const estreito = useWindowDimensions().width < 430;   // celular: o botão Feriados mostra só o 🎉
  const sheet = useTaskSheet();
  const [date, setDate] = useState(params.date ?? today());
  const [now, setNow] = useState(new Date());
  const [mesAberto, setMesAberto] = useState(false);
  const [busca, setBusca] = useState(false);
  const [ferAberto, setFerAberto] = useState(false);

  useEffect(() => { if (params.date) setDate(params.date); }, [params.date]);
  // 0.72 (texto 11a, A): o fim de todo "voltar" é o My Planner no dia de hoje
  useEffect(() => ouvirVoltarAoPlanner(() => setDate(today())), []);
  // No My Planner, o "voltar" do Android não faz nada (não sai do app nem abre outra aba). As janelas daqui
  // (Modal) fecham pelo próprio onRequestClose, que não passa por aqui.
  const foco = useIsFocused();
  useEffect(() => {
    if (Platform.OS !== 'android' || !foco) return;
    const s = BackHandler.addEventListener('hardwareBackPress', () => true);
    return () => s.remove();
  }, [foco]);
  // texto 12: chegou aqui pelo "voltar" do Android vindo de outra aba → dia de hoje (como o gesto do iPhone)
  useEffect(() => { if (foco && tomarVoltarAndroid()) setDate(today()); }, [foco]);
  useEffect(() => { diaAberto.atual = date; }, [date]);
  useEffect(() => { const i = setInterval(() => setNow(new Date()), 30000); return () => clearInterval(i); }, []);

  const todasDoDia = useMemo(() => tasksOn(date, tasks), [date, tasks]);
  const viagem = useViagemDoDia(date);
  const pendentes = usePendentes();
  // durante a viagem, com "Só o roteiro", ficam só as tarefas da própria aba Viagens
  const doDia = viagem.so ? todasDoDia.filter(x => x.area === 'viagens') : todasDoDia;
  // 0.65: arquivadas saem da lista; sem horário, as fixadas vêm primeiro
  const org = useOrganizar('planner');
  const { ativos: semHora, arquivados: arqSem } = org.separar(doDia.filter(t => !t.time), x => x.id);
  const { ativos: comHora, arquivados: arqCom } = org.separar(doDia.filter(t => t.time), x => x.id, () => false);
  const arquivadas = [...arqSem, ...arqCom];
  const excluirTarefas = (ids: string[]) => ids.forEach(id => {
    const tk = doDia.find(x => x.id === id); if (!tk) return;
    // texto 05 C: a que se repete sai só deste dia; tudo vai para a Lixeira, com o Desfazer
    excluirTarefa(id, isRepeating(tk) ? 'one' : 'all', date);
  });
  const selSem = useSelecao({ itens: semHora, chave: x => x.id, cor: accent, onArquivar: ids => org.arquivar(ids), onExcluir: excluirTarefas });
  const selCom = useSelecao({ itens: comHora, chave: x => x.id, cor: accent, onArquivar: ids => org.arquivar(ids), onExcluir: excluirTarefas });
  const energia = doDia.reduce((s, t) => s + (t.energy || 0), 0);
  const e = ajustes.energia;
  const limite = e.modo === 'sem' ? null : e.modo === 'todos' ? e.todos : e.dias[parseYmd(date).getDay()];
  const passou = limite != null && energia > limite;
  const d = parseYmd(date);

  return (
    <View style={{ flex: 1, backgroundColor: C.bg, paddingTop: ins.top + 8 }}>
      <View style={{ paddingHorizontal: 16, gap: 10 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          <Pressable onPress={() => setMesAberto(true)} hitSlop={6} style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flexShrink: 1 }}>
            <Text numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.7} style={{ color: C.text, fontSize: 26, fontWeight: '800', textTransform: 'capitalize', flexShrink: 1 }}>{nomeMes(d.getMonth())}</Text>
            <Text numberOfLines={1} style={{ color: C.sub, fontSize: 26, fontWeight: '300' }}>{d.getFullYear()}</Text>
            <Text style={{ color: C.sub, fontSize: 14 }}>▾</Text>
          </Pressable>
          <Pressable onPress={() => router.push('/ajustes?sec=energia' as any)} hitSlop={10}
            style={{ flexDirection: 'row', alignItems: 'center', gap: 2, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 14,
              backgroundColor: passou ? 'rgba(240,82,82,0.18)' : C.card }}>
            <Text style={{ color: passou ? C.danger : C.text, fontWeight: '800' }}>{energia}{limite != null ? `/${limite}` : ''}</Text>
            <Text style={{ fontSize: 14 }}>{passou ? '🔴' : '⚡'}</Text>
          </Pressable>
          {ajustes.feriadosTopo !== false && <Pressable onPress={() => setFerAberto(true)} hitSlop={8} accessibilityLabel={t('Feriados')}
            style={{ flexDirection: 'row', alignItems: 'center', gap: 3, paddingHorizontal: 8, paddingVertical: 5, borderRadius: 14, backgroundColor: C.card }}>
            <Text style={{ fontSize: 12 }}>🎉</Text>
            {!estreito && <Text numberOfLines={1} style={{ color: C.text, fontSize: 12, fontWeight: '700' }}>{t('Feriados')}</Text>}
          </Pressable>}
          <View style={{ flex: 1 }} />
          <Pressable onPress={() => setBusca(true)} hitSlop={10} style={{ width: 38, height: 38, borderRadius: 19, backgroundColor: C.card, alignItems: 'center', justifyContent: 'center' }}>
            <Text style={{ fontSize: 16 }}>🔍</Text>
          </Pressable>
        </View>
        <WeekStrip date={date} onChange={setDate} cor={accent} marcas={dd => tasksOn(dd, tasks).length} feriado={dd => feriados.doDia(dd).length > 0} />
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14 }}>
          <Pressable onPress={() => setMesAberto(true)} hitSlop={6} style={{ flex: 1 }}>
            <Text style={{ color: C.sub, fontSize: 13 }}>{labelDia(date)} ▾</Text>
          </Pressable>
          <Pressable onPress={() => setDate(addDays(date, -7))} hitSlop={10}><Text style={{ color: C.sub, fontSize: 22 }}>‹</Text></Pressable>
          <Pressable onPress={() => setDate(today())} hitSlop={6}><Text style={{ color: accent, fontWeight: '700' }}>{t('Hoje')}</Text></Pressable>
          <Pressable onPress={() => setDate(addDays(date, 7))} hitSlop={10}><Text style={{ color: C.sub, fontSize: 22 }}>›</Text></Pressable>
        </View>
      </View>

      <ScrollArrastavel contentContainerStyle={{ padding: 16, paddingBottom: 140, gap: m.gap(12) }}>
        <AtualizacaoBanner cor={accent} />
        <CronoCard cor={accent} />
        {(() => { const s = situacao('planner'); return s.tipo === 'teste' && s.restam <= 15 ? (
          <Pressable onPress={() => router.push('/ajustes' as any)} style={{ padding: 12, borderRadius: 14, backgroundColor: 'rgba(245,158,11,0.14)', borderWidth: 1, borderColor: 'rgba(245,158,11,0.4)' }}>
            <Text style={{ color: C.text, fontWeight: '700' }}>⏳ {t('Faltam {n} dia(s) do seu período grátis', { n: s.restam + 1 })}</Text>
            <Text style={{ color: C.sub, fontSize: 12 }}>{t('Veja os planos em Ajustes › Compras.')}</Text>
          </Pressable>) : null; })()}
        {(() => {
          const fs = feriados.doDia(date);
          if (!fs.length) return null;
          // agrupa o mesmo feriado em várias cidades (ex.: Corpus Christi em Curitiba e São Paulo)
          const grupos = fs.reduce<Record<string, typeof fs>>((g, f) => { (g[f.pais + f.nome + f.tipo] ??= []).push(f); return g; }, {});
          return Object.values(grupos).map(g => {
            const f = g[0];
            const facult = f.tipo === 'facultativo';
            return (
              <View key={f.pais + f.nome + f.tipo} style={{ flexDirection: 'row', alignItems: 'center', gap: 12, padding: 12, borderRadius: 14,
                backgroundColor: facult ? 'rgba(245,158,11,0.12)' : 'rgba(248,113,113,0.14)', borderWidth: 1, borderColor: facult ? 'rgba(245,158,11,0.4)' : 'rgba(248,113,113,0.45)' }}>
                <Text style={{ fontSize: 22 }}>{f.tipo === 'nacional' ? bandeira(f.pais) : f.tipo === 'estadual' || f.tipo === 'regional' ? '🏛️' : f.tipo === 'municipal' ? '🏙️' : '📌'}</Text>
                <View style={{ flex: 1 }}>
                  <Text style={{ color: C.text, fontWeight: '700' }}>{t(f.nome)}</Text>
                  <Text style={{ color: C.sub, fontSize: 12 }}>{t(ROTULO[f.tipo])}{f.tipo !== 'nacional' && f.tipo !== 'facultativo' ? ' · ' + g.map(x => t(x.local)).join(', ') : f.pais !== 'BR' ? ' · ' + nomePais(f.pais) : ''}</Text>
                </View>
              </View>
            );
          });
        })()}
        <ViagemHoje dia={date} escondidas={todasDoDia.length - doDia.length} />
        {pendentes > 0 && (
          <Pressable onPress={() => abrirGrupos({ recebidos: true })} style={{ flexDirection: 'row', alignItems: 'center', gap: 12, padding: 12, borderRadius: 14, backgroundColor: 'rgba(139,92,246,0.16)', borderWidth: 1, borderColor: 'rgba(139,92,246,0.5)' }}>
            <Text style={{ fontSize: 22 }}>📥</Text>
            <View style={{ flex: 1 }}>
              <Text style={{ color: C.text, fontWeight: '700' }}>{t('{n} recebido(s) dos seus grupos', { n: pendentes })}</Text>
              <Text style={{ color: C.sub, fontSize: 12 }}>{t('Toque para aceitar, editar ou recusar.')}</Text>
            </View>
            <Text style={{ color: accent, fontWeight: '800' }}>{t('Ver')}</Text>
          </Pressable>
        )}
        {date === today() && <Pendentes />}
        {date === today() && <AguardandoEnvio />}
        <Caixinhas dia={date} />
        {semHora.length > 0 && (
          <View style={{ gap: m.gap(8) }}>
            <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8 }}>{t('SEM HORÁRIO')}</Text>
            {selSem.barra}
            <Ordenavel itens={semHora} chave={x => x.id} onOrdem={novos => ordenarTarefas(novos.map(x => x.id))} selecao={selSem.prop}
              render={x => <TaskCardAcoes t={x} date={date} now={now} compact />} />
          </View>
        )}
        {comHora.length > 0 && <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8, marginTop: 4 }}>{t('LINHA DO TEMPO')}</Text>}
        {selCom.barra}
        <Ordenavel itens={comHora} chave={x => x.id} desligado gap={0} onOrdem={() => {}} selecao={selCom.prop} render={(x, i) => (
          <View>
            <TaskCardAcoes t={x} date={date} now={now} />
            {i < comHora.length - 1 && <View style={{ width: 2, height: m.gap(14), backgroundColor: C.line, marginLeft: m.v(14, 13, 11) }} />}
          </View>
        )} />
        <Arquivados itens={arquivadas} chave={x => x.id} cor={accent}
          render={x => <Text style={{ color: C.text }} numberOfLines={2}>{x.emoji} {x.title}{x.time ? ' · ' + horaTela(x.time) : ''}</Text>}
          onRestaurar={x => org.restaurar(x.id)} onExcluir={x => Alert.alert(t('Excluir tarefa?'), [x.title, t('Fica 30 dias na lixeira.')].join('\n\n'), [
            { text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: () => excluirTarefas([x.id]) }])} />
        {!doDia.length && !viagem.viagem && (
          <Pressable onPress={() => sheet.open({ date })} style={{ marginTop: 40, alignItems: 'center', gap: 8 }}>
            <Text style={{ fontSize: 42 }}>🌤️</Text>
            <Text style={{ color: C.text, fontSize: 16, fontWeight: '600' }}>{t('Nada planejado para este dia')}</Text>
            <Text style={{ color: accent }}>{t('Toque aqui ou no + para criar uma tarefa')}</Text>
          </Pressable>
        )}
      </ScrollArrastavel>
      <MonthSheet visible={mesAberto} date={date} tasks={tasks} cor={accent} onPick={setDate} onClose={() => setMesAberto(false)}
        feriado={dd => { const f = feriados.doDia(dd); return f.length ? [...new Set(f.map(x => t(x.nome)))].join(', ') : null; }} />
      <FeriadosSheet visible={ferAberto} date={date} tasks={tasks} cor={accent} onClose={() => setFerAberto(false)} onIrPara={setDate} />
      <SearchSheet visible={busca} tasks={tasks} cor={accent} onClose={() => setBusca(false)}
        onOpen={(t, dd) => { setDate(dd); diaAberto.atual = dd; sheet.open({ task: t, date: dd }); }} />
    </View>
  );
}
